import { debounce, flatten } from 'lodash-es';
import { RenderState } from '../types/RenderState.type';
import { LAYOUT, PDF_PRELOAD_STRATEGY, ASSET_TYPES_WITH_MAX_1_RENDERER_IN_HORIZONTAL_LAYOUT } from '../constants/Constants';
import { browser } from '../helpers/BrowserHelper';
import { LayoutFactory } from './LayoutFactory';
import { LayoutHorizontal } from './layouts/LayoutHorizontal';
import { AssetViewerPresentationRenderer } from './renderers/AssetViewerPresentationRenderer';
import { PdfJsPresentationRenderer } from './renderers/PdfJsPresentationRenderer';
import { PdfViewerPresentationRenderer } from './renderers/PdfViewerPresentationRenderer';
import { AssetViewer } from './AssetViewer';
import { AssetViewerViewMapper } from '../helpers/AssetViewerViewMapper';
import { ViewerEventType, ViewerUpdateEvent, ViewerFocusChangeEvent, ViewerRenderedViewFocusEvent, ViewerRenderedPreviewFocusEvent, ViewerPublicFocusChangeEvent, ViewerPublicRenderedViewFocusEvent, ViewerPublicRenderedPreviewFocusEvent, ViewerViewportChangeEvent, ViewerAddViewsEvent, ViewerEnableNavigationWithinCurrentAssetViewerItemEvent, ViewerDisableNavigationWithinCurrentAssetViewerItemEvent, ViewerThumbnailsOpenedChangedEvent } from '../types/ViewerEvents.type';
let instance = 0;
export class Viewer {
    constructor(config, eventBus, assetViewerItems, tokenService, renderingQueue, isThumbnailViewer, linkService, pdfJsLinkService) {
        this.config = config;
        this.eventBus = eventBus;
        this.assetViewerItems = assetViewerItems;
        this.tokenService = tokenService;
        this.renderingQueue = renderingQueue;
        this.isThumbnailViewer = isThumbnailViewer;
        this.linkService = linkService;
        this.pdfJsLinkService = pdfJsLinkService;
        this.isDestroyed = false;
        this.active = true;
        this.debouncedUpdate = debounce(this.update.bind(this), this.config.VIEWPORT_UPDATE_DEBOUNCE_TIMEOUT);
        this.viewsLoadedWhileIdle = 0;
        this.navigationOnlyWithinCurrentAssetViewerItemEnabled = false;
        this.instance = instance;
        instance++;
        this.viewerLayout = LayoutFactory.create(this.config.VIEWER_LAYOUT, this.config, this.eventBus, this.assetViewerItems, this.tokenService, this.linkService, this.pdfJsLinkService);
        this.idleTimeout = null;
        // State
        this.onViewportUpdate = this.onViewportUpdate.bind(this);
        this.onFocusChange = this.onFocusChange.bind(this);
        this.onRenderedViewFocus = this.onRenderedViewFocus.bind(this);
        this.onRenderedPreviewFocus = this.onRenderedPreviewFocus.bind(this);
        this.onViewRendered = this.onViewRendered.bind(this);
        this.onPreviewRendered = this.onPreviewRendered.bind(this);
        this.onNavigateToPage = this.onNavigateToPage.bind(this);
        this.onLayoutZoomChange = this.onLayoutZoomChange.bind(this);
        this.onIdle = this.onIdle.bind(this);
        this.addEventListeners();
    }
    get layout() {
        return this.config.VIEWER_LAYOUT;
    }
    init(container, assetIndexToFocus, page) {
        this.viewerLayout.render(container);
        let viewToFocus;
        if (typeof assetIndexToFocus === 'number' && assetIndexToFocus > -1) {
            if (typeof page === 'number') {
                viewToFocus = this.viewerLayout.views.find(view => view.assetViewerItem === this.assetViewerItems[assetIndexToFocus] && view.page === page);
            }
            else {
                viewToFocus = this.viewerLayout.views.find(view => view.assetViewerItem === this.assetViewerItems[assetIndexToFocus]);
            }
        }
        if (viewToFocus) {
            this.currentView = viewToFocus;
        }
        else if (typeof assetIndexToFocus === 'number' && assetIndexToFocus > -1 && typeof page === 'number') {
            // set page to be focussed when a nested AssetViewer gets initialized
            const parentView = this.viewerLayout.views.find(view => this.assetViewerItems[assetIndexToFocus] === view.assetViewerItem);
            this.currentView = parentView;
            if (parentView && parentView.presentationRenderer instanceof AssetViewerPresentationRenderer) {
                const viewToFocus = parentView.presentationRenderer.assetViewer.viewer.views.find(view => view.assetViewerItem === this.assetViewerItems[assetIndexToFocus] && view.page === page);
                if (viewToFocus) {
                    parentView.presentationRenderer.pageToInit = viewToFocus.page;
                }
            }
            // set page to be focussed when a nested PdfViewer gets initialized
            if (parentView && parentView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                if (page) {
                    parentView.presentationRenderer.initialPageNumber = page;
                }
            }
        }
        if (!this.currentView) {
            this.currentView = this.views[0];
        }
        // start update cycle after viewerLayout is rendered and the view to focus is set
        this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this.viewerLayout.viewState }));
    }
    set currentViewIndex(index) {
        this.currentView = this.views[index];
    }
    set currentView(view) {
        this.viewerLayout.currentView = view;
        this._setCurrentView(view);
    }
    get currentView() {
        return this._currentView;
    }
    get views() {
        return this.viewerLayout.views;
    }
    get allViews() {
        return flatten(this.viewerLayout.views.map(view => view.presentationRenderer && view.presentationRenderer.assetViewer instanceof AssetViewer
            ? [view].concat(view.presentationRenderer.assetViewer.viewer.views)
            : view));
    }
    get allViewsWithParents() {
        return flatten(this.viewerLayout.views.map(view => view.presentationRenderer && view.presentationRenderer.assetViewer instanceof AssetViewer
            ? view.presentationRenderer.assetViewer.viewer.views.map(childView => ({ view: childView, parentView: view }))
            : { view }));
    }
    setActive() {
        if (this.active) {
            return;
        }
        this.active = true;
        this.viewerLayout.addEventListeners();
        this.addEventListeners();
        this.update();
        this.views.forEach(view => {
            if (view.presentationRenderer && view.presentationRenderer.assetViewer) {
                view.presentationRenderer.assetViewer.setActive();
            }
        });
    }
    setPassive() {
        if (!this.active) {
            return;
        }
        this.active = false;
        this.viewerLayout.removeEventListeners();
        this.removeEventListeners();
        this.views.forEach(view => {
            if (view.presentationRenderer && view.presentationRenderer.assetViewer /* instanceof AssetViewer*/) {
                view.presentationRenderer.assetViewer.setPassive();
            }
        });
    }
    update() {
        this.eventBus.dispatch(new ViewerUpdateEvent());
        if (this.isDestroyed || this.config.IS_SLAVE_ASSET_VIEWER) {
            return;
        }
        if (this.config.DEBUG) {
            console.log('update', this.instance);
        }
        if (!this.active) {
            return;
        }
        this.renderingQueue.renderHighestPriority();
    }
    forceRendering(view, renderPreview = false) {
        if (this.isDestroyed) {
            return false;
        }
        let highestPriorityView = view;
        let highestPriorityViews = highestPriorityView ? [highestPriorityView] : null;
        if (!highestPriorityViews) {
            // Don't try to render views before the layout is rendered
            if (!this.viewerLayout.container) {
                return false;
            }
            const viewState = this.viewerLayout.viewState;
            if (!viewState || typeof viewState !== 'object') {
                throw new Error('viewState not an object');
            }
            if (this.config.RENDER_VISIBLE_VIEWS_CONCURRENTLY) {
                highestPriorityViews = this.renderingQueue.getHighestPriorityViews(viewState);
            }
            else {
                highestPriorityView = this.renderingQueue.getHighestPriorityView(viewState);
                highestPriorityViews = highestPriorityView ? [highestPriorityView] : null;
            }
        }
        if (highestPriorityViews && highestPriorityViews.length) {
            highestPriorityViews.forEach(highestPriorityView => {
                const highestPriorityAssetViewerView = highestPriorityView.view;
                if (highestPriorityAssetViewerView.shouldLoad) {
                    highestPriorityAssetViewerView
                        .load()
                        .then(metadata => {
                        if (metadata && metadata.numViews) {
                            if (metadata.numViews !== highestPriorityAssetViewerView.assetViewerItem.numViews &&
                                highestPriorityAssetViewerView.assetViewerItem.numViews === 1 &&
                                !highestPriorityAssetViewerView.assetViewerItem.page // Don't add views if assetViewerItem has only 1 page
                            ) {
                                // Used in case of pdfjs if we don"t know the amount of pages before rendering the viewer
                                const numViews = metadata.numViews;
                                const suggestedWidth = metadata.width;
                                const suggestedHeight = metadata.width;
                                highestPriorityAssetViewerView.assetViewerItem.numViews = numViews;
                                highestPriorityAssetViewerView.eventBus.dispatch(new ViewerAddViewsEvent({
                                    view: highestPriorityAssetViewerView,
                                    numViews,
                                    suggestedWidth,
                                    suggestedHeight
                                }));
                                // TODO fix this any cast
                                const viewsToAdd = highestPriorityAssetViewerView.viewerLayout.addViews(highestPriorityAssetViewerView, numViews, suggestedWidth, suggestedHeight);
                                const viewer = highestPriorityView && highestPriorityView.parentView
                                    ? highestPriorityView.parentView.presentationRenderer.assetViewer.viewer
                                    : this;
                                if (viewer.currentView === highestPriorityAssetViewerView) {
                                    viewer.currentView = viewsToAdd[0];
                                }
                            }
                            if (metadata.numViews !== highestPriorityAssetViewerView.assetViewerItem.numViews &&
                                highestPriorityAssetViewerView.assetViewerItem.numViews !== 1) {
                                throw 'offset in numViews between SvgPresentation and PdfJsPresentation';
                            }
                            return highestPriorityAssetViewerView.viewerLayout.update();
                        }
                        // update even when numViews didn't change, width and height could have changed
                        return highestPriorityAssetViewerView.viewerLayout.update(highestPriorityAssetViewerView.viewerLayout.views.indexOf(highestPriorityAssetViewerView));
                    })
                        // TODO check if this animationframe is an improvement
                        .then(() => requestAnimationFrame(() => this.update()), error => {
                        console.error(error);
                        requestAnimationFrame(() => this.update());
                    });
                    return true;
                }
                // TODO make this work with previewRendering
                // this.renderingQueue.cancelNonVisibleViews(viewState, this.views);
                this.renderingQueue.renderView(highestPriorityAssetViewerView, renderPreview);
                // });
            });
            return true;
        }
        return false;
    }
    addEventListeners() {
        this.eventBus.on(ViewerEventType.ViewportChange, this.onViewportUpdate);
        this.eventBus.on(ViewerEventType.FocusChange, this.onFocusChange);
        this.eventBus.on(ViewerEventType.RenderedViewFocus, this.onRenderedViewFocus);
        this.eventBus.on(ViewerEventType.RenderedPreviewFocus, this.onRenderedPreviewFocus);
        this.eventBus.on(ViewerEventType.ViewRendered, this.onViewRendered);
        this.eventBus.on(ViewerEventType.PreviewRendered, this.onPreviewRendered);
        this.eventBus.on(ViewerEventType.NavigateToPage, this.onNavigateToPage);
        this.eventBus.on(ViewerEventType.LayoutZoomChange, this.onLayoutZoomChange);
    }
    removeEventListeners() {
        this.eventBus.off(ViewerEventType.ViewportChange, this.onViewportUpdate);
        this.eventBus.off(ViewerEventType.FocusChange, this.onFocusChange);
        this.eventBus.off(ViewerEventType.RenderedViewFocus, this.onRenderedViewFocus);
        this.eventBus.off(ViewerEventType.RenderedPreviewFocus, this.onRenderedPreviewFocus);
        this.eventBus.off(ViewerEventType.ViewRendered, this.onViewRendered);
        this.eventBus.off(ViewerEventType.PreviewRendered, this.onPreviewRendered);
        this.eventBus.off(ViewerEventType.NavigateToPage, this.onNavigateToPage);
        this.eventBus.off(ViewerEventType.LayoutZoomChange, this.onLayoutZoomChange);
    }
    onViewportUpdate(event) {
        const focussedView = event.payload.viewState.focus.view;
        const viewIndex = this.views.indexOf(focussedView);
        if (focussedView && viewIndex !== -1) {
            // TODO fix this ugly code
            if (this.viewerLayout instanceof LayoutHorizontal && !this.viewerLayout.currentView) {
                this.viewerLayout.currentView = focussedView;
            }
            this._setCurrentView(focussedView);
        }
        this.debouncedUpdate();
        this.viewsLoadedWhileIdle = 0;
    }
    onRenderedViewFocus(event) {
        const assetViewerView = event.payload.assetViewerView;
        if (assetViewerView && this.publicAssetViewerViewIds.includes(assetViewerView.id)) {
            this.eventBus.dispatch(new ViewerPublicRenderedViewFocusEvent({ assetViewerView }));
        }
    }
    onRenderedPreviewFocus(event) {
        const assetViewerView = event.payload.assetViewerView;
        if (assetViewerView && this.publicAssetViewerViewIds.includes(assetViewerView.id)) {
            this.eventBus.dispatch(new ViewerPublicRenderedPreviewFocusEvent({ assetViewerView }));
        }
    }
    onFocusChange(event) {
        const assetViewerView = event.payload.assetViewerView;
        if (assetViewerView.assetViewerItem.type !== 'document') {
            this.eventBus.dispatch(new ViewerThumbnailsOpenedChangedEvent({ opened: false }));
        }
        if (assetViewerView && this.publicAssetViewerViewIds.includes(assetViewerView.id)) {
            this.eventBus.dispatch(new ViewerPublicFocusChangeEvent({ assetViewerView }));
        }
    }
    onPreviewRendered(event) {
        const assetViewerView = event.payload.assetViewerView;
        if (this.currentView.id === assetViewerView.id) {
            this.eventBus.dispatch(new ViewerRenderedPreviewFocusEvent({ assetViewerView }));
        }
    }
    onViewRendered(event) {
        const assetViewerView = event.payload.assetViewerView;
        if (this.currentView.id === assetViewerView.id) {
            this.eventBus.dispatch(new ViewerRenderedViewFocusEvent({ assetViewerView }));
        }
    }
    onNavigateToPage(event) {
        const page = event.payload.page;
        const currentView = this.currentView;
        if (currentView) {
            const viewToNavigateTo = this.views.filter(view => view.assetViewerItem.id === currentView.assetViewerItem.id && view.page === page)[0];
            if (viewToNavigateTo) {
                this.currentView = viewToNavigateTo;
            }
        }
    }
    onLayoutZoomChange(event) {
        this.debouncedUpdate();
    }
    _setCurrentView(view) {
        if (this.currentView === view) {
            return;
        }
        this._currentView = view;
        this.eventBus.dispatch(new ViewerFocusChangeEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(view) }));
        if (view.renderState === RenderState.Finished) {
            this.eventBus.dispatch(new ViewerRenderedViewFocusEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(view) }));
        }
        if (view.previewRenderState === RenderState.Finished) {
            this.eventBus.dispatch(new ViewerRenderedPreviewFocusEvent({ assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(view) }));
        }
    }
    getAllVisibleViews(viewState) {
        return flatten(viewState.views
            .map(visibleView => visibleView.view)
            .map(assetViewerView => {
            if (assetViewerView.presentationRenderer && assetViewerView.presentationRenderer.assetViewer instanceof AssetViewer) {
                if (assetViewerView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState &&
                    assetViewerView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.views.length) {
                    const visibleNestedViews = assetViewerView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.views.map(visibleView => visibleView.view);
                    // return [assetViewerView].concat(visibleNestedViews);
                    return visibleNestedViews.map(view => ({ view, parentView: assetViewerView }));
                }
                else {
                    return { view: assetViewerView };
                }
            }
            else {
                return { view: assetViewerView };
            }
        }));
    }
    getFocussedView(viewState) {
        const focussedView = viewState.focus.view;
        if (focussedView.presentationRenderer &&
            focussedView.presentationRenderer.assetViewer instanceof AssetViewer &&
            focussedView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState &&
            focussedView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.focus.view) {
            return {
                view: focussedView.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.focus.view,
                parentView: focussedView
            };
        }
        else {
            return { view: focussedView };
        }
    }
    shouldRenderPreview(view) {
        return view.previewRenderState === RenderState.Initial && view.renderState === RenderState.Initial;
    }
    getLowerPriorityView(viewState) {
        const viewsThatShouldBeLoaded = this.getViewsToBeLoaded(viewState);
        const viewsThatShouldBeLoadedThatAreNotYetBeingLoaded = viewsThatShouldBeLoaded.filter(view => this.shouldRenderPreview(view.view));
        const focussedView = this.getFocussedView(viewState).view;
        // TODO navigation direction should take nested assetViewer into consideration
        const navigatingForward = true;
        const sortedViews = viewsThatShouldBeLoadedThatAreNotYetBeingLoaded.sort((view1, view2) => this.sortByLessPriority(focussedView, view1.view, view2.view, navigatingForward));
        if (sortedViews.length === 0) {
            return;
        }
        const parentView = sortedViews[sortedViews.length - 1].parentView;
        // TODO take parent view into account
        return parentView && this.renderingQueue.shouldRenderView(parentView) ? { view: parentView } : sortedViews[sortedViews.length - 1];
    }
    getViewsToBeLoaded(viewState) {
        const visibleViews = this.getAllVisibleViews(viewState);
        const numberOfVisibleViews = visibleViews.length;
        const firstVisibleViewIndex = this.allViewsWithParents.findIndex(view => view.view === viewState.first.view);
        const lastView = viewState.last.view.presentationRenderer && viewState.last.view.presentationRenderer.assetViewer instanceof AssetViewer
            ? viewState.last.view.presentationRenderer.assetViewer.viewer.viewerLayout.viewState &&
                viewState.last.view.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.last.view
                ? viewState.last.view.presentationRenderer.assetViewer.viewer.viewerLayout.viewState.last.view
                : viewState.last.view
            : viewState.last.view;
        const lastVisibleViewIndex = this.allViewsWithParents.findIndex(view => view.view === lastView);
        const canLoadViewBeforeFirstVisibleView = firstVisibleViewIndex > 0;
        const canLoadViewAfterLastVisibleView = lastVisibleViewIndex < this.allViewsWithParents.length - 1;
        const maxNumberOfViewsToLoad = browser.mobile ? this.config.VIEWS_TO_PRELOAD_MOBILE : this.config.VIEWS_TO_PRELOAD;
        const minNumberOfViewsToLoad = numberOfVisibleViews + (canLoadViewBeforeFirstVisibleView ? 1 : 0) + (canLoadViewAfterLastVisibleView ? 1 : 0);
        const numberOfViewsToLoad = Math.max(maxNumberOfViewsToLoad, minNumberOfViewsToLoad);
        const viewsToLoadBeforeFirstVisibleView = canLoadViewBeforeFirstVisibleView
            ? [this.allViewsWithParents[firstVisibleViewIndex - 1]]
            : [];
        // apply preload strategies to views before visible views
        const filteredViewsToLoadBeforeFirstVisibleView = this.filterViewsThatCanBePreloaded(viewsToLoadBeforeFirstVisibleView);
        const numberOfViewsToLoadAfterLastVisibleView = numberOfViewsToLoad - visibleViews.length - filteredViewsToLoadBeforeFirstVisibleView.length;
        const viewsToLoadAfterLastVisibleView = this.allViewsWithParents.slice(lastVisibleViewIndex + 1, lastVisibleViewIndex + numberOfViewsToLoadAfterLastVisibleView + 1);
        // apply preload strategies to views after visible views
        const filteredViewsToLoadAfterLastVisibleView = this.filterViewsThatCanBePreloaded(viewsToLoadAfterLastVisibleView);
        const viewsToBeLoaded = filteredViewsToLoadBeforeFirstVisibleView.concat(visibleViews, filteredViewsToLoadAfterLastVisibleView);
        return viewsToBeLoaded;
    }
    filterViewsThatCanBePreloaded(viewsToBeLoaded) {
        const registeredViewsToBeLoadedPerAssetType = new Map();
        return viewsToBeLoaded.filter(viewToBeLoaded => {
            const { type } = viewToBeLoaded.view.assetViewerItem;
            if (!registeredViewsToBeLoadedPerAssetType.has(type)) {
                registeredViewsToBeLoadedPerAssetType.set(type, 0);
            }
            if (this.config.VIEWS_TO_PRELOAD_PER_ASSET_TYPE[type] === undefined) {
                // no strategy defined => passes the filter
                return true;
            }
            const max = this.config.VIEWS_TO_PRELOAD_PER_ASSET_TYPE[type];
            const registered = registeredViewsToBeLoadedPerAssetType.get(type) || 0;
            if (registered < max) {
                const updatedNumber = registeredViewsToBeLoadedPerAssetType.get(type) + 1;
                registeredViewsToBeLoadedPerAssetType.set(type, updatedNumber);
                return true;
            }
            else {
                return false;
            }
        });
    }
    isViewVisible(view) {
        const visibleViews = this.viewerLayout.viewState.views.map(visibleView => visibleView.view);
        return visibleViews.indexOf(view) > -1;
    }
    destroyViewsIfNecessary(viewState) {
        // destroying views is handled by the main viewer
        if (this.config.IS_SLAVE_ASSET_VIEWER) {
            return;
        }
        const viewsWithNonInitialRenderState = this.allViewsWithParents.filter(({ view }) => view.renderState !== RenderState.Initial);
        const viewsThatShouldBeLoaded = this.getViewsToBeLoaded(viewState).map(view => view.view);
        const shouldDestroyViews = viewsWithNonInitialRenderState.length > viewsThatShouldBeLoaded.length;
        if (shouldDestroyViews) {
            const viewsThatCanBeDestroyed = viewsWithNonInitialRenderState.filter(({ view }) => viewsThatShouldBeLoaded.indexOf(view) === -1 && !this.isViewVisible(view) && view.canBeDestroyed);
            const canDestroyViews = viewsThatCanBeDestroyed.length > this.config.BATCH_DESTROY_VIEWS_COUNT;
            if (canDestroyViews) {
                try {
                    viewsThatCanBeDestroyed.forEach(view => {
                        view.view.destroy();
                        if (view.parentView && !this.isViewVisible(view.parentView) && view.parentView.canBeDestroyed) {
                            view.parentView.destroy();
                        }
                    });
                }
                catch (error) {
                    console.error(error);
                }
            }
        }
        // SP-10568 Ensure we do not have multiple active videos
        // SP-55408 URL video asset continues to play when switching assets
        if (this.config.VIEWER_LAYOUT === LAYOUT.HORIZONTAL) {
            this.views.map(view => {
                if (ASSET_TYPES_WITH_MAX_1_RENDERER_IN_HORIZONTAL_LAYOUT.includes(view.assetViewerItem.type) &&
                    view !== this.currentView &&
                    view.renderState !== RenderState.Initial) {
                    view.destroy();
                }
            });
        }
    }
    sortByLessPriority(focussedView, view1, view2, navigatingForward) {
        const viewInScrollDirection1 = this.isViewPositionedInScrollDirection(focussedView, view1, navigatingForward);
        const viewInScrollDirection2 = this.isViewPositionedInScrollDirection(focussedView, view2, navigatingForward);
        if (viewInScrollDirection1 === viewInScrollDirection2) {
            const distance1 = Math.abs(this.allViews.indexOf(focussedView) - this.allViews.indexOf(view1));
            const distance2 = Math.abs(this.allViews.indexOf(focussedView) - this.allViews.indexOf(view2));
            return distance1 === distance2 ? 0 : distance1 < distance2 ? 1 : -1;
        }
        return viewInScrollDirection1 === true ? 1 : -1;
    }
    isViewPositionedInScrollDirection(focussedView, view, navigatingForward) {
        return navigatingForward === true
            ? this.allViews.indexOf(focussedView) <= this.allViews.indexOf(view)
            : this.allViews.indexOf(focussedView) >= this.allViews.indexOf(view);
    }
    onIdle() {
        if (this.isThumbnailViewer) {
            return;
        }
        if (this.config.DEBUG) {
            console.log('idle', this.instance);
        }
        const viewState = this.viewerLayout.viewState;
        this.destroyViewsIfNecessary(viewState); // TODO check if this covers views of the thumbnailViewer
        const lowerPriorityView = this.getLowerPriorityView(viewState);
        // SPW-6985
        if (lowerPriorityView && lowerPriorityView.view && this.renderingQueue.shouldRenderView(lowerPriorityView.view)) {
            // this.update(viewState, lowerPriorityView);
            if (this.config.DEBUG) {
                console.log('render low prio view', lowerPriorityView, this.instance, lowerPriorityView.view.page);
            }
            if (lowerPriorityView.view.presentationRenderer instanceof PdfJsPresentationRenderer) {
                if (this.config.PDF_PRELOAD_STRATEGY === PDF_PRELOAD_STRATEGY.LOW_PREVIEW ||
                    this.config.PDF_PRELOAD_STRATEGY === PDF_PRELOAD_STRATEGY.HIGH_PREVIEW) {
                    return this.forceRendering(lowerPriorityView, true);
                }
                if (this.config.PDF_PRELOAD_STRATEGY === PDF_PRELOAD_STRATEGY.FULL) {
                    return this.forceRendering(lowerPriorityView);
                }
            }
            else {
                return this.forceRendering(lowerPriorityView);
            }
        }
    }
    canNavigateToNext() {
        if (this.navigationOnlyWithinCurrentAssetViewerItemEnabled) {
            return this.removePageFromViewId(this.currentViewId) === this.removePageFromViewId(this.nextViewId);
        }
        return !!this.nextViewId;
    }
    canNavigateToPrevious() {
        if (this.navigationOnlyWithinCurrentAssetViewerItemEnabled) {
            return this.removePageFromViewId(this.currentViewId) === this.removePageFromViewId(this.previousViewId);
        }
        return !!this.previousViewId;
    }
    next() {
        // TODO instead of the currentView (= focussed view), use first view?
        if (this.currentView) {
            if (this.currentView.presentationRenderer instanceof PdfViewerPresentationRenderer &&
                this.currentView.presentationRenderer.pdfViewer &&
                this.currentView.presentationRenderer.pdfViewer.canNavigateToNext()) {
                this.currentView.presentationRenderer.pdfViewer.next();
            }
            else if (this.currentView.presentationRenderer instanceof AssetViewerPresentationRenderer &&
                this.currentView.presentationRenderer.assetViewer.canNavigateToNext()) {
                this.currentView.presentationRenderer.assetViewer.next();
            }
            else {
                const nextIndex = this.canNavigateToNext() ? this.views.indexOf(this.currentView) + 1 : 0;
                const nextView = this.views[nextIndex];
                if (nextView.presentationRenderer instanceof AssetViewerPresentationRenderer) {
                    if (this.renderingQueue.shouldRenderView(nextView)) {
                        this.renderingQueue.renderView(nextView);
                    }
                    else {
                        const nestedView = nextView.presentationRenderer.assetViewer.viewer.views[0];
                        if (nextView.presentationRenderer.assetViewer.viewer.currentView === nestedView) {
                            this.eventBus.dispatch(new ViewerFocusChangeEvent({
                                assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(nestedView)
                            }));
                            if (nestedView.renderState === RenderState.Finished) {
                                this.eventBus.dispatch(new ViewerRenderedViewFocusEvent({
                                    assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(nestedView)
                                }));
                            }
                        }
                        nextView.presentationRenderer.assetViewer.viewer.currentView = nestedView;
                    }
                }
                if (nextView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                    nextView.presentationRenderer.initialPageNumber = 'FIRST';
                }
                this.currentView = nextView;
            }
            this.viewerLayout.lastNavigatedNext = true;
        }
    }
    previous() {
        if (this.currentView) {
            if (this.currentView.presentationRenderer instanceof PdfViewerPresentationRenderer &&
                this.currentView.presentationRenderer.pdfViewer &&
                this.currentView.presentationRenderer.pdfViewer.canNavigateToPrevious()) {
                this.currentView.presentationRenderer.pdfViewer.previous();
            }
            else if (this.currentView.presentationRenderer instanceof AssetViewerPresentationRenderer &&
                this.currentView.presentationRenderer.assetViewer.canNavigateToPrevious()) {
                this.currentView.presentationRenderer.assetViewer.previous();
            }
            else {
                const previousIndex = this.canNavigateToPrevious() ? this.views.indexOf(this.currentView) - 1 : this.views.length - 1;
                const previousView = this.views[previousIndex];
                if (previousView.presentationRenderer instanceof AssetViewerPresentationRenderer) {
                    if (this.renderingQueue.shouldRenderView(previousView)) {
                        const viewIndex = previousView.presentationRenderer.assetViewer.viewer.views.length - 1;
                        previousView.presentationRenderer.pageToInit =
                            previousView.presentationRenderer.assetViewer.viewer.views[viewIndex].page;
                        this.renderingQueue.renderView(previousView);
                    }
                    else {
                        const nestedView = previousView.presentationRenderer.assetViewer.viewer.views[previousView.presentationRenderer.assetViewer.viewer.views.length - 1];
                        if (previousView.presentationRenderer.assetViewer.viewer.currentView === nestedView) {
                            this.eventBus.dispatch(new ViewerFocusChangeEvent({
                                assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(nestedView)
                            }));
                            if (nestedView.previewRenderState === RenderState.Finished) {
                                this.eventBus.dispatch(new ViewerRenderedPreviewFocusEvent({
                                    assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(nestedView)
                                }));
                            }
                            if (nestedView.renderState === RenderState.Finished) {
                                this.eventBus.dispatch(new ViewerRenderedViewFocusEvent({
                                    assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(nestedView)
                                }));
                            }
                        }
                        previousView.presentationRenderer.assetViewer.viewer.currentView = nestedView;
                    }
                }
                if (previousView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                    previousView.presentationRenderer.initialPageNumber = 'LAST';
                }
                this.currentView = previousView;
            }
            this.viewerLayout.lastNavigatedNext = false;
        }
    }
    // TODO Checks for nested viewer need to also check for PDFViewer
    navigateToAssetViewerItem(assetViewerItemId, page) {
        const ensuredPage = page || 1;
        const viewIndexToFocus = this.views.findIndex(view => view.assetViewerItem.id === assetViewerItemId && view.page === ensuredPage);
        if (viewIndexToFocus === -1) {
            const viewToFocus = this.views.find(view => view.assetViewerItem.id === assetViewerItemId);
            if (viewToFocus) {
                if (viewToFocus.presentationRenderer instanceof AssetViewerPresentationRenderer) {
                    const viewToNavigateTo = viewToFocus.presentationRenderer.assetViewer.viewer.views.find(view => view.assetViewerItem.id === assetViewerItemId && view.page === ensuredPage);
                    if (viewToNavigateTo) {
                        if (viewToFocus.presentationRenderer.assetViewer.viewer.currentView === viewToNavigateTo) {
                            viewToFocus.presentationRenderer.pageToInit = viewToNavigateTo.page;
                            viewToFocus.presentationRenderer.assetViewer.viewer.currentView = viewToNavigateTo;
                            this.eventBus.dispatch(new ViewerFocusChangeEvent({
                                assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(viewToNavigateTo)
                            }));
                            if (viewToNavigateTo.previewRenderState === RenderState.Finished) {
                                this.eventBus.dispatch(new ViewerRenderedPreviewFocusEvent({
                                    assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(viewToNavigateTo)
                                }));
                            }
                            if (viewToNavigateTo.renderState === RenderState.Finished) {
                                this.eventBus.dispatch(new ViewerRenderedViewFocusEvent({
                                    assetViewerView: AssetViewerViewMapper.mapToPublicAssetViewerView(viewToNavigateTo)
                                }));
                            }
                        }
                        else {
                            viewToFocus.presentationRenderer.pageToInit = viewToNavigateTo.page;
                            viewToFocus.presentationRenderer.assetViewer.viewer.currentView = viewToNavigateTo;
                        }
                        // TODO this is not always true
                        viewToFocus.presentationRenderer.assetViewer.viewer.viewerLayout.lastNavigatedNext = true;
                    }
                }
                if (viewToFocus.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                    const pdfViewerPresentationRenderer = viewToFocus.presentationRenderer;
                    const pageId = `${assetViewerItemId}-${page}`;
                    if (pdfViewerPresentationRenderer.pdfViewer) {
                        pdfViewerPresentationRenderer.pdfViewer.goto(pageId);
                    }
                    else {
                        pdfViewerPresentationRenderer.initialPageNumber = page;
                    }
                }
                this.currentView = viewToFocus;
            }
        }
        else if (typeof viewIndexToFocus === 'number') {
            this.currentViewIndex = viewIndexToFocus;
            // TODO this is not always true
            this.viewerLayout.lastNavigatedNext = true;
        }
    }
    destroy() {
        this.isDestroyed = true;
        this.removeEventListeners();
        this.viewerLayout.destroy();
        this.views.forEach((view) => view.destroyFinal());
    }
    resetAnnotations() {
        this.views.forEach((view) => view.resetAnnotations());
    }
    // Trigger resize for viewer and nested viewers
    resize() {
        if (this.viewerLayout.viewState) {
            this.destroyViewsIfNecessary(this.viewerLayout.viewState);
        }
        this.viewerLayout.resize();
        this.views.forEach(view => view.resize());
        this.update();
    }
    getAssetViewerViews() {
        const assetViewerViews = [];
        this.views.forEach(assetViewerView => {
            if (assetViewerView.presentationRenderer instanceof AssetViewerPresentationRenderer) {
                assetViewerViews.push(...assetViewerView.presentationRenderer.getAssetViewerViews());
            }
            else if (assetViewerView.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                assetViewerViews.push(...assetViewerView.presentationRenderer.getAssetViewerViews());
            }
            else {
                assetViewerViews.push(AssetViewerViewMapper.mapToPublicAssetViewerView(assetViewerView));
            }
        });
        return assetViewerViews;
    }
    get currentViewId() {
        if (this.currentView.presentationRenderer instanceof AssetViewerPresentationRenderer &&
            this.currentView.presentationRenderer.assetViewer &&
            this.currentView.presentationRenderer.assetViewer.viewer &&
            this.currentView.presentationRenderer.assetViewer.viewer.currentView) {
            return this.currentView.presentationRenderer.assetViewer.viewer.currentView.id;
        }
        if (this.currentView.presentationRenderer instanceof PdfViewerPresentationRenderer &&
            this.currentView.presentationRenderer.pdfViewer) {
            return this.currentView.presentationRenderer.pdfViewer.pageIdFocused;
        }
        return this.currentView.id;
    }
    get previousViewId() {
        const currentViewId = this.currentViewId;
        const assetViewerViewIds = this.getAssetViewerViews().map(view => view.id);
        return assetViewerViewIds[assetViewerViewIds.indexOf(currentViewId) - 1];
    }
    get nextViewId() {
        const currentViewId = this.currentViewId;
        const assetViewerViewIds = this.getAssetViewerViews().map(view => view.id);
        return assetViewerViewIds[assetViewerViewIds.indexOf(currentViewId) + 1];
    }
    get publicAssetViewerViewIds() {
        return this.getAssetViewerViews().map(assetViewerView => assetViewerView.id);
    }
    enableNavigationOnlyWithinCurrentAssetViewerItem() {
        this.navigationOnlyWithinCurrentAssetViewerItemEnabled = true;
        this.eventBus.dispatch(new ViewerEnableNavigationWithinCurrentAssetViewerItemEvent());
    }
    disableNavigationOnlyWithinCurrentAssetViewerItem() {
        this.navigationOnlyWithinCurrentAssetViewerItemEnabled = false;
        this.eventBus.dispatch(new ViewerDisableNavigationWithinCurrentAssetViewerItemEvent());
    }
    removePageFromViewId(viewId) {
        return viewId ? viewId.split('-')[0] : '';
    }
}
//# sourceMappingURL=Viewer.js.map